import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonAvatar from "components/ArgonAvatar";
import ArgonBadge from "components/ArgonBadge";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, Tooltip } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import axios, { baseURL } from "utils/api";
import { toast } from "react-toastify";

// Profile cell
function Author({ image, name, id }) {
  const navigate = useNavigate();

  return (
    <ArgonBox display="flex" alignItems="center" px={0.5} py={0.1}>
      <ArgonBox mr={2}>
        <ArgonAvatar
          src={image}
          alt={name}
          size="sm"
          variant="rounded"
          sx={{
            "& img": {
              objectFit: "cover",
              width: "100%",
              height: "100%",
            },
          }}
        />
      </ArgonBox>
      <ArgonBox display="flex" flexDirection="column">
        <ArgonTypography
          textTransform="none"
          variant="button"
          fontWeight="medium"
          onClick={() => navigate(`/parents/profile/${id}`)}
          sx={{
            cursor: "pointer",
            textDecoration: "underline",
            "&:hover": {
              color: "#1976d2",
              textDecoration: "underline",
            },
          }}
        >
          {name}
        </ArgonTypography>
      </ArgonBox>
    </ArgonBox>
  );
}

Author.propTypes = {
  image: PropTypes.string,
  name: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
};

// Function to get city and area from coordinates using reverse geocoding
const getAddressFromCoordinates = async (lat, lng) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
    );
    const data = await response.json();
    const city = data.address?.city || data.address?.town || data.address?.village || "-";
    const area = data.address?.suburb || data.address?.neighbourhood || "-";
    return { city, area };
  } catch (error) {
    console.error("Error fetching address:", error);
    return { city: "-", area: "-" };
  }
};
const StudentsList = ({ students = [] }) => {
  if (students.length === 0) {
    return (
      <ArgonTypography variant="caption" color="error">
        No Students
      </ArgonTypography>
    );
  }

  const displayStudent = students[0]; // first student
  const extraCount = students.length - 1; // extra students count

  const content = (
    <ArgonBox display="flex" alignItems="center" gap={1}>
      <ArgonBox display="flex" flexDirection="column" alignItems="flex-start">
        <ArgonTypography
          textTransform="none"
          sx={{
            backgroundColor: "#f0f0f0",
            padding: "4px 7px",
            borderRadius: "6px",
          }}
          variant="caption"
          fontWeight="medium"
          color="dark"
        >
          {displayStudent} {extraCount > 0 && `+${extraCount}`}
        </ArgonTypography>
      </ArgonBox>
    </ArgonBox>
  );

  const tooltipContent = (
    <ArgonBox sx={{ maxHeight: "200px", overflowY: "auto", padding: "4px" }}>
      <ArgonTypography
        variant="caption"
        fontWeight="bold"
        color="white"
        sx={{ display: "block", marginBottom: "8px" }}
      >
        All Students ({students.length}):
      </ArgonTypography>
      <ArgonBox>
        {students.map((student, index) => (
          <ArgonTypography
            key={index}
            variant="caption"
            color="white"
            sx={{ display: "block", marginBottom: "4px" }}
          >
            • {student}
          </ArgonTypography>
        ))}
      </ArgonBox>
    </ArgonBox>
  );

  return (
    <Tooltip
      title={tooltipContent}
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            backdropFilter: "blur(8px)",
            borderRadius: "8px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
            animation: "fadeInUp 0.3s ease-out",
            "@keyframes fadeInUp": {
              "0%": {
                opacity: 0,
                transform: "translateY(10px) scale(0.95)",
              },
              "100%": {
                opacity: 1,
                transform: "translateY(0) scale(1)",
              },
            },
          },
        },
        arrow: {
          sx: {
            color: "rgba(0, 0, 0, 0.9)",
          },
        },
      }}
    >
      <ArgonBox sx={{ cursor: "pointer" }}>{content}</ArgonBox>
    </Tooltip>
  );
};

StudentsList.propTypes = {
  students: PropTypes.arrayOf(PropTypes.string),
};

const useParentsData = (handleDeleteClick, refresh) => {
  const [rows, setRows] = useState([]);

  const columns = [
    { name: "sno", align: "center" },
    { name: "profile", align: "left" },
    { name: "students", align: "left" },
    { name: "class Name", align: "left" },
    { name: "register Date", align: "left" },
    { name: "city", align: "left" },
    { name: "area", align: "left" },
    { name: "action", align: "center" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${baseURL}/student`);
        console.log("Students API response:", res.data);
        if (res.data.success) {
          const data = res.data.data; // students array from backend

          if (!data || data.length === 0) {
            setRows([
              {
                sno: (
                  <ArgonTypography variant="caption" fontWeight="medium" color="text">
                    -
                  </ArgonTypography>
                ),
                profile: "-",
                students: "-",
                "class Name": "-",
                "register Date": "-",
                city: "-",
                area: "-",
                action: "-",
              },
            ]);
          } else {
            const parentsMap = {};
            data.forEach((student) => {
              const parentId = student?.parentId?._id;

              // Skip if parent doesn't exist or is deleted
              if (!student?.parentId || !parentId) {
                console.log("Skipping student - no parent:", student?.name);
                return;
              }

              if (
                student?.parentId?.isDeleted === true ||
                student?.parentId?.isDeleted === "true"
              ) {
                console.log("Skipping deleted parent:", student?.parentId?.fullName);
                return;
              }

              if (!parentsMap[parentId]) {
                parentsMap[parentId] = {
                  parent: student?.parentId?.fullName || "-",
                  students: [],
                  classes: [],
                  registerDate: student?.parentId?.createdAt
                    ? new Date(student.parentId.createdAt).toLocaleDateString()
                    : "-",
                  city: student?.parentId?.city || "-",
                  area: student?.parentId?.area || "-",
                  parentId: parentId,
                  coordinates: student?.parentId?.location?.coordinates || null,
                  profileImage: student?.parentId?.profileImage || null,
                  isDeleted: student?.parentId?.isDeleted,
                };
              }
              parentsMap[parentId].students.push(student?.name || "-");
              parentsMap[parentId].classes.push(student?.class?.name || "-");
            });

            const formattedRows = Object.values(parentsMap).map((parent, index) => ({
              sno: (
                <ArgonTypography variant="caption" fontWeight="medium" color="text">
                  {index + 1}
                </ArgonTypography>
              ),

              profile: (
                <Author image={parent.profileImage} name={parent.parent} id={parent.parentId} />
              ),

              students: <StudentsList students={parent.students} />,

              "class Name": (
                <ArgonTypography
                  variant="caption"
                  color="text"
                  fontWeight="medium"
                  sx={{ padding: "4px" }}
                >
                  {parent.classes.join(", ")}
                </ArgonTypography>
              ),

              "register Date": (
                <ArgonTypography
                  variant="caption"
                  color="text"
                  fontWeight="medium"
                  sx={{ padding: "4px" }}
                >
                  {parent.registerDate}
                </ArgonTypography>
              ),

              city: (
                <ArgonTypography variant="caption" color="text" fontWeight="medium">
                  {parent.city}
                </ArgonTypography>
              ),

              area: (
                <ArgonTypography variant="caption" color="text" fontWeight="medium">
                  {parent.area}
                </ArgonTypography>
              ),

              status: (
                <ArgonBadge
                  variant="gradient"
                  badgeContent={parent.status}
                  color={parent.status === "Active" ? "success" : "secondary"}
                  size="xs"
                  container
                />
              ),

              action: (
                <ArgonBox display="flex" alignItems="center" gap={0}>
                  {/* View */}
                  <Button
                    variant="text"
                    size="large"
                    component={Link}
                    to={`/parents/profile/${parent.parentId}`}
                    sx={{ minWidth: "12px", p: 0 }}
                  >
                    <VisibilityIcon color="primary" />
                  </Button>

                  {/* Delete */}
                  <Button
                    variant="text"
                    size="large"
                    sx={{ minWidth: "32px", p: 0 }}
                    onClick={() => handleDeleteClick(parent.parentId)}
                  >
                    <DeleteIcon color="error" />
                  </Button>
                </ArgonBox>
              ),
            }));

            setRows(formattedRows);
          }
        }
      } catch (err) {
        console.error("❌ Error fetching students:", err);
        toast.error(err.response?.data?.error || "Failed to load students");
      }
    };

    fetchData();
  }, [refresh]);

  return { columns, rows };
};

export default useParentsData;
